import { type FC, memo, useCallback, useMemo } from "react";

import { parseGid, ProductProvider, useProduct } from "@shopify/hydrogen-react";

import { ProductHighlightedUpsellView } from "@react/sections/ProductHero/HighlightedUpsell/ProductHighlightedUpsell.view";
import { STOREFRONT_CART_ATTRIBUTES } from "@root/tablescape/constants";
import {
  type CartProductVariant,
  type CartProductHighlightedUpsell,
  type UseProduct,
} from "@root/tablescape/state";
import {
  formatProduct,
  formatProductVariant,
} from "@root/tablescape/utilities/product";

const ProductHighlightedUpsellRootBase = () => {
  const originProduct = useProduct();

  const formattedOriginProduct = useMemo(() => {
    return formatProduct(originProduct.product);
  }, [originProduct.product]);

  const highlightedUpsell = useMemo(() => {
    return formattedOriginProduct.highlightedUpsell;
  }, [originProduct.product]);

  const productSelectorId = useMemo(() => {
    return formattedOriginProduct.productSelectorId;
  }, [originProduct.product]);

  if (
    !originProduct?.product.highlightedUpsell?.reference?.product?.reference ||
    !highlightedUpsell ||
    productSelectorId
  ) {
    return null;
  }

  return (
    <ProductProvider
      data={
        originProduct.product?.highlightedUpsell?.reference?.product?.reference
      }
    >
      <ProductHighlightedUpsellBase
        highlightedUpsell={highlightedUpsell}
        originProduct={originProduct}
      />
    </ProductProvider>
  );
};

type Props = {
  highlightedUpsell: CartProductHighlightedUpsell;
  originProduct: UseProduct;
};

const ProductHighlightedUpsellBase: FC<Props> = ({
  highlightedUpsell,
  originProduct,
}) => {
  const { product, selectedVariant, setSelectedVariant } = useProduct();
  const formattedProduct = useMemo(() => formatProduct(product), [product]);
  const currentVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const products = useMemo(() => {
    return [formatProduct(originProduct.product), formatProduct(product)];
  }, [originProduct, product]);
  const variants = useMemo(() => {
    return [
      formatProductVariant(originProduct.selectedVariant),
      formatProductVariant(selectedVariant),
    ];
  }, [originProduct, selectedVariant]);

  const price = useMemo(() => {
    return {
      amount: `${
        parseFloat(selectedVariant.price.amount) -
        (parseFloat(highlightedUpsell.discountPrice?.amount ?? "0") || 0)
      }`,
      currencyCode: selectedVariant.price.currencyCode || "USD",
    };
  }, [selectedVariant, highlightedUpsell.discountPrice]);

  const compareAtPrice = useMemo(() => {
    if (
      !selectedVariant.compareAtPrice &&
      parseFloat(price.amount) < parseFloat(selectedVariant.price.amount)
    ) {
      return selectedVariant.price;
    }
    return selectedVariant.compareAtPrice;
  }, [price.amount, selectedVariant.price, selectedVariant.compareAtPrice]);

  const attributes = useMemo(() => {
    const isMultiQuantitySet =
      [...new Set(variants.map((variant) => variant.id))].length === 1;

    const variantsToUse = isMultiQuantitySet ? variants.slice(0, 1) : variants;

    return [
      {
        key: STOREFRONT_CART_ATTRIBUTES.RELATED_ITEMS,
        value: variantsToUse
          .map((variant) => parseGid(variant.id).id)
          .join(";"),
      },
    ];
  }, [variants]);

  const selectVariant = useCallback(
    (variantId: CartProductVariant["id"]) => {
      const selectedVariant = product.variants.nodes.find(
        (variant) => variant.id === variantId
      );

      if (selectedVariant) {
        setSelectedVariant(selectedVariant);
      }
    },
    [setSelectedVariant, product]
  );

  return (
    <ProductHighlightedUpsellView
      attributes={attributes}
      badgeText={highlightedUpsell.badgeText}
      buttonText={highlightedUpsell.buttonText}
      compareAtPrice={compareAtPrice}
      currentVariant={currentVariant}
      price={price}
      product={formattedProduct}
      products={products}
      selectVariant={selectVariant}
      title={highlightedUpsell.title}
      variants={variants}
    />
  );
};

export const ProductHighlightedUpsell = memo(ProductHighlightedUpsellRootBase);
